var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useEffect, useState, useCallback, useRef, } from 'react';
import { Box, Paper, Grid, } from '@mui/material';
import { useTranslation } from 'react-i18next';
import lodashDebounce from 'lodash.debounce';
import * as ls from 'local-storage';
import 'katex/dist/katex.min.css';
import { getAuthHeader, UserContext } from '../auth';
import { axiosClient } from '../axios';
import { chatStyles } from './Chat.styles';
import { LOG_LEVELS, SERVICES, ENDPOINTS, } from '../constants';
import { MessagesBox } from './MessagesBox';
import { ChatInstructions } from './ChatInstructions';
import { ChatOngoingMessage } from './ChatOngoingMessage';
import { ChatMessageInput } from './ChatMessageInput';
/**
 * Chat component
 * @param props Chat props
 * @returns Chat
 */
export const Chat = function (props) {
    var _a, _b, _c;
    // Get user credential
    const user = useContext(UserContext);
    // Get translation function
    const { t, i18n } = useTranslation();
    // Extract chat props
    const { documentId, chat, setChat, editorTitle, handleDocumentUpdate, sendMessage, waiting, resContent, outputLanguage, showEditor, isDownMDBreakPoint, setWorkflow, handleShowWorkflowClick, setWorkflowOpen, } = props;
    // Auto Save Chat debounce
    const autoSaveChatDebounce = useCallback(lodashDebounce((documentId, newChat) => {
        try {
            if (!newChat) {
                // If newChat is null/undefined, dont save the chat
                return;
            }
            // Stop if documentId was not loaded
            if (!documentId) {
                return;
            }
            ;
            // Get current document
            const document = JSON.parse(ls.get(documentId) || '{ }');
            // Update new document
            const newDocument = Object.assign(document, { chat: JSON.parse(JSON.stringify(newChat)) });
            // Save new document to local storage
            ls.set(documentId, JSON.stringify(newDocument));
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, autoSaveChatDebounce: ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, 900), []);
    // Update chat title of it's empty
    useEffect(() => {
        try {
            if (!(chat === null || chat === void 0 ? void 0 : chat.title) && editorTitle) {
                chat.title = editorTitle;
                setChat(Object.assign({}, chat));
            }
        }
        catch (err) {
            // Save error to the log system
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, useEffect,: ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [editorTitle]);
    // Save chat when chat title or messages change
    useEffect(() => {
        try {
            if (!documentId) {
                // If documentId was not loaded
                // Stop
                return;
            }
            if (!chat) {
                // If chat was not loaded
                return;
            }
            // Call auto save chat debounce, notify the document
            autoSaveChatDebounce(documentId, chat);
            handleDocumentUpdate();
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [chat, chat && chat.messages.length, chat === null || chat === void 0 ? void 0 : chat.title, chat === null || chat === void 0 ? void 0 : chat.aiModel, chat === null || chat === void 0 ? void 0 : chat.brandVoiceId]);
    // Scroll to bottom of the chat when the chat change
    useEffect(() => {
        try {
            // Get chat messages element
            const chatMessagesElement = document.getElementById('chat-messages');
            if (chatMessagesElement) {
                // Scroll to bottom
                chatMessagesElement.scrollTop = chatMessagesElement.scrollHeight;
            }
        }
        catch (err) {
            // Save error to the system log
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, useEffect, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [(_a = chat === null || chat === void 0 ? void 0 : chat.messages) === null || _a === void 0 ? void 0 : _a.length, resContent === null || resContent === void 0 ? void 0 : resContent.length]);
    // Messages Block Ref
    const messageBlockRef = useRef(null);
    // Messages Block Height
    const [messageBlockHeight, setMessageBlockHeight] = useState(96);
    // Fetch and update document when user credential changed
    useEffect(() => {
        /**
         * Get list of brand voices
         * @returns {Promise<void>}
         */
        const getBrandVoices = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                if (!user) {
                    return;
                }
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                // Get brand voices results
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.CHAT}${ENDPOINTS.GET_BRAND_VOICES}`, { headers: Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language }) });
                // Extract brand voices from the resulst
                const brandVoices = ((_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.brandVoices) || [];
                // Create brand voice options for the Brand voice select input
                const brandVoiceOptions = brandVoices.map((brandVoice) => {
                    return {
                        title: brandVoice.name,
                        value: brandVoice._id,
                        content: brandVoice.content,
                    };
                });
                // Update the brand voice options
                setBrandVoiceOptions([defaultBrandVoiceOption, ...brandVoiceOptions]);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Chat, getBrandvoices, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.AI_CHAT,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getBrandVoices();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Chat, useEffect, getBrandVoices, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.AI_CHAT,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator.userAgent,
                },
            });
        }
        ;
    }, [user]);
    // Default brand voice
    const defaultBrandVoice = { name: 'No Brand Voice', content: '', _id: '0' };
    // Default brand voice option for the select input
    const defaultBrandVoiceOption = { title: 'No Brand Voice', content: '', value: '0' };
    // List of brand voice options for the select input
    const [brandVoiceOptions, setBrandVoiceOptions] = useState([defaultBrandVoiceOption]);
    // The selected brand voice option for the select input
    const [selectedBrandVoiceOption, setSelectedBrandVoiceOption] = useState(defaultBrandVoiceOption);
    useEffect(() => {
        // Get selected brand voice index from the brand voices list
        const selectedBrandVoiceIndex = brandVoiceOptions.findIndex((brandVoiceOption) => ((brandVoiceOption || '0').value.toString() === ((chat === null || chat === void 0 ? void 0 : chat.brandVoiceId) || '0').toString()));
        // Update the selected brand voice if it's existed in the brand voices list
        if (selectedBrandVoiceIndex >= 0) {
            setSelectedBrandVoiceOption(Object.assign({}, brandVoiceOptions[selectedBrandVoiceIndex]));
        }
    }, [chat === null || chat === void 0 ? void 0 : chat.brandVoiceId, brandVoiceOptions]);
    return (_jsx(Paper
    // Chat container
    , Object.assign({ 
        // Chat container
        sx: chatStyles.container }, { children: _jsxs(Grid
        // Chat Grid container
        , Object.assign({ 
            // Chat Grid container
            container: true, sx: {
                height: '100%',
                position: 'relative',
            } }, { children: [_jsx(Grid
                // Chat grid messages
                , Object.assign({ 
                    // Chat grid messages
                    item: true, xs: 12, sx: {
                        height: `calc(100%)`,
                        overflow: 'hidden',
                    } }, { children: _jsxs(Box
                    // Chat messages block
                    , Object.assign({ 
                        // Chat messages block
                        id: "chat-messages", sx: {
                            padding: `0 0 ${messageBlockHeight + 24}px`,
                            height: '100%',
                            overflow: 'auto',
                            boxSizing: 'border-box',
                        } }, { children: [_jsx(ChatInstructions, { display: ((_b = chat === null || chat === void 0 ? void 0 : chat.messages) === null || _b === void 0 ? void 0 : _b.length) > 0 ? 'none' : 'block', setWorkflow: setWorkflow, handleShowWorkflowClick: handleShowWorkflowClick, setWorkflowOpen: setWorkflowOpen }), _jsx(Box, Object.assign({ sx: {
                                    display: ((_c = chat === null || chat === void 0 ? void 0 : chat.messages) === null || _c === void 0 ? void 0 : _c.length) > 0 ? 'block' : 'none',
                                } }, { children: _jsxs(Grid, Object.assign({ container: true, rowSpacing: 1, sx: {
                                        alignItems: 'flex-start',
                                        alignContent: 'flex-start'
                                    } }, { children: [_jsx(MessagesBox, { messages: (chat === null || chat === void 0 ? void 0 : chat.messages) || [], brandVoiceContent: selectedBrandVoiceOption === null || selectedBrandVoiceOption === void 0 ? void 0 : selectedBrandVoiceOption.content, sendMessage: sendMessage }), _jsx(Grid, Object.assign({ item: true, xs: 12, sx: { display: waiting ? 'block' : 'none', textAlign: 'left' } }, { children: _jsx(ChatOngoingMessage, { resContent: resContent }) }))] })) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(ChatMessageInput, { chat: chat, setChat: setChat, sendMessage: sendMessage, waiting: waiting, outputLanguage: outputLanguage, showEditor: showEditor, setMessageBlockHeight: setMessageBlockHeight }) }))] })) })));
};
